import React, { type ReactElement, type ReactNode, useEffect, useState, useRef } from 'react'
import { StyledDataGrid, StyledTableWrapper } from './styles'
import type { GridAlignment, GridRenderCellParams, GridValidRowModel, GridTreeNodeWithRender, GridRowParams, GridSortModel, GridSortItem } from '@mui/x-data-grid'
import { useTheme } from '@mui/material/styles'
import { type CustomColumnDef, type Row, type CustomTableProps, TableModalAction, Actions } from './types'
import TableTopBarActions from './TableTopBarActions'
import { generateColumns } from './generateColumns'
import { v4 as uuidv4 } from 'uuid'
import CustomPagination from './CustomPagination'
import ActionsWithDropdown from './CellTypes/ActionsWithDropdown'
import GenericModal from '../GenericModal'
import NoDataFound from './NoDataFound'
import { Box, CircularProgress, Typography } from '@mui/material'
import { blue, grey } from '../../colors'
import { ButtonType } from '../GenericButton/styles'
import lodash from 'lodash'
import { CellWarningType } from './CellTypes/CellWarning'
const CustomTable = <RowType extends Row>({
  rows,
  columns,
  label,
  createActionTitle = '',
  createAction,
  topbarTitle,
  enableAllSelection = false,
  hideSelectedColumnCount = true,
  featureName = '',
  editAction,
  deleteAction,
  errorAction,
  archiveAction,
  extraColumnProps,
  extraModalProps,
  onRowClick,
  onEditClick,
  onPreviewClick,
  onGoLiveClick,
  customTopbarAction,
  createButtonProps,
  onSearch = () => {},
  handlePageChange = () => { },
  handleRowsPerPageChange = () => { },
  loading = false,
  noDataComponentProps,
  list,
  overrides = {},
  onSortModelChange,
  actionModalEntityDisplayField = 'title',
  createModalProps,
  editModalProps,
  deleteModalProps,
  errorModalProps,
  exportCsv,
  exportButtonProps = {},
  params = {},
  setParams = () => {},
  archiveModalProps
}: CustomTableProps<RowType>): JSX.Element => {
  const theme = useTheme()
  const [selectedRow, setSelectedRow] = useState<Row | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [preparedColumns, setPreparedColumns] = useState<CustomColumnDef[]>([])
  const [searchText, setSearchText] = useState<string | null>(null)
  const [sortModel, setSortModel] = useState<GridSortModel>([])
  const [actionModal, setActionModal] = useState<TableModalAction | null>(null)

  const previousActionModalRef = useRef<TableModalAction | null>(null)
  const { pagination } = list ?? {}
  const tableRows = rows ?? list?.data ?? []
  const tableColumns = columns ?? list?.keys ?? []

  const entityDisplayField = lodash.get(selectedRow, actionModalEntityDisplayField)
  const deleteModalTitle = entityDisplayField ? `Delete "${entityDisplayField}"?` : 'Delete'
  const archiveModalTitle = entityDisplayField ? `Archive "${entityDisplayField}"?` : 'Archive'

  const errorType = previousActionModalRef.current?.toLowerCase()
  const errorModalTitle = entityDisplayField ? `Cannot ${errorType as unknown as string} "${entityDisplayField}"` : 'Delete'

  const handleSearch = (value: string): void => {
    const { pageNumber, ...restParams } = params
    setParams({
      ...restParams,
      search: value
    })
    setSearchText(value)
    onSearch(value)
  }

  const closeActionModal = (): void => {
    setActionModal(null)
    previousActionModalRef.current = actionModal
  }

  const EditDeleteArchiveActionWithModal: any = {
    field: 'actions',
    type: 'actions',
    align: 'right' as GridAlignment,
    minWidth: 180,
    flex: 1,
    renderCell: (params: GridRenderCellParams<GridValidRowModel, any, any, GridTreeNodeWithRender>): React.ReactNode => {
      const rowActions = params?.row?.actions

      if (rowActions?.length === 0) {
        return <></>
      }
      return (
        <ActionsWithDropdown
          handleSelectedRow={() => { setSelectedRow(params.row.data) }}
          rowActions={rowActions}
          onEditClick={onEditClick}
          onPreviewClick={onPreviewClick}
          onGoLiveClick={onGoLiveClick}
          setActionModal={setActionModal}
          params={params}
        />
      )
    }
  }

  useEffect(() => {
    if (Array.isArray(tableColumns)) {
      const preparedCols = generateColumns(tableColumns, extraColumnProps, overrides, setSelectedRow, setActionModal)
      setPreparedColumns(preparedCols)
    }
  }, [tableColumns])

  const generateRowId = (): string => {
    return `row-${uuidv4()}`
  }

  const tableTopBarProps = {
    onSearch: handleSearch,
    createButtonProps,
    customTopbarAction,
    label,
    title: topbarTitle ?? list?.header?.headerName,
    createActionTitle,
    list,
    searchText,
    exportCsv,
    exportButtonProps,
    handleCreateActionModal: () => { setActionModal(TableModalAction.CREATE) }
  }

  const rowHeight = 56

  const setTableHeight = (): string => {
    const pageSize = pagination?.pageSize ?? 5
    const tableHeight = pageSize * rowHeight + 69
    return `${tableHeight}px`
  }

  const handleSortModelChange = (newSortModel: GridSortModel): void => {
    if (newSortModel.length > 0) {
      setParams({
        ...params,
        sort: JSON.stringify(newSortModel.map((sort: GridSortItem) => ({
          direction: sort.sort,
          field: sort.field
        })))
      })
      setSortModel(newSortModel)
      void onSortModelChange?.(newSortModel[0])
    }
  }

  const cloneElementWithProps = (
    element: ReactElement<any> | undefined,
    props?: Record<string, any>
  ): ReactNode => {
    if (!element) return null

    const defaultProps = {
      handleCloseModal: closeActionModal,
      showErrorModal: () => { previousActionModalRef.current = actionModal; setActionModal(TableModalAction.ERROR) },
      setErrorMessage
    }
    return React.cloneElement(element, { ...defaultProps, ...props })
  }

  const onRowDoubleClick = (params: GridRowParams): void => {
    const editAction = params?.row?.actions?.includes(Actions.EDIT)
    if (!editAction) { return }
    onEditClick ? onEditClick(params.row) : setActionModal(TableModalAction.EDIT); setSelectedRow(params.row.data)
  }

  const getRowClassName = (params: GridRowParams): string => {
    const warningMsg = params.row?.data?.warningMessage
    if (warningMsg) {
      const keys = Object.keys(warningMsg)
      for (const k of keys) {
        if (warningMsg[k]?.key === CellWarningType.WARNING) return 'row-warning-bg'
        if (warningMsg[k]?.key === CellWarningType.ERROR) return 'row-error-bg'
      }
    }
    return ''
  }

  return (
    <>
      <StyledTableWrapper>
        <TableTopBarActions {...tableTopBarProps} />
        {
          !loading && tableRows?.length === 0 && <NoDataFound list={list} noDataComponentProps={noDataComponentProps} searchText={params?.search ?? searchText} />
        }

        {
          loading && <Box display='flex' justifyContent='center' alignItems='center' minHeight={theme.spacing(80)}>
            <CircularProgress thickness={4} value={2} sx={{ color: blue[500] }} />
          </Box>
        }
        {
          !loading && preparedColumns?.length > 0 && (tableRows?.length > 0) &&
          <>
            <StyledDataGrid
              tableHeight={setTableHeight()}
              isRowSelectable={(params: GridRowParams) => params.row?.actions?.length > 0}
              getRowId={generateRowId}
              columns={[...preparedColumns, EditDeleteArchiveActionWithModal]}
              rows={tableRows}
              checkboxSelection={enableAllSelection}
              hideFooterPagination={true}
              hideFooterSelectedRowCount={hideSelectedColumnCount}
              onRowClick={onRowClick}
              onRowDoubleClick={onRowDoubleClick}
              rowHeight={rowHeight}
              onSortModelChange={handleSortModelChange}
              sortingMode="server"
              sortModel={sortModel}
              getRowClassName={getRowClassName}
              disableColumnMenu
            />

            {pagination && (
              <CustomPagination
                {...pagination}
                handlePageChange={(pageNumber) => {
                  setParams({
                    ...params,
                    pageNumber
                  })
                  void handlePageChange(pageNumber)
                }}
                handleRowsPerPageChange={(pageSize) => {
                  setParams({
                    ...params,
                    pageSize
                  })
                  void handleRowsPerPageChange(pageSize)
                }}
              />
            )}
          </>
        }

        {/* CREATE MODAL  */}
        <GenericModal
          isOpen={actionModal === TableModalAction.CREATE}
          title={`Create ${featureName}`}
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          showCloseIcon={false}
          action={false}
          {...extraModalProps}
          {...createModalProps}
        >
          {cloneElementWithProps(createAction)}
        </GenericModal>

        {/* EDIT MODAL  */}
        <GenericModal
          isOpen={actionModal === TableModalAction.EDIT}
          title={`Edit ${featureName}`}
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          showCloseIcon={true}
          action={false}
          {...extraModalProps}
          {...editModalProps}
        >
          {cloneElementWithProps(editAction, { selectedRow })}
        </GenericModal>

        {/* DELETE MODAL  */}
        <GenericModal
          showCloseIcon={false}
          isOpen={actionModal === TableModalAction.DELETE}
          title={deleteModalTitle}
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          modalWidth={theme.spacing(136)}
          action={false}
          {...extraModalProps}
          {...deleteModalProps}
        >
          {cloneElementWithProps(deleteAction, { selectedRow })}
        </GenericModal>

        {/* Error MODAL  */}
        <GenericModal
          showCloseIcon={false}
          isOpen={actionModal === TableModalAction.ERROR}
          title={errorModalTitle}
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          modalWidth={theme.spacing(136)}
          action={true}
          hideConfirmButton
          destructiveButtonText='Close'
          destructiveButtonType={ButtonType.Primary}
          {...extraModalProps}
          {...errorModalProps}
        >
          {
            errorAction
              ? cloneElementWithProps(errorAction, { selectedRow })
              : <Typography fontSize={'14px'} color={grey[850]}>{errorMessage}</Typography>
          }
        </GenericModal>

        {/* Archive MODAL  */}
        <GenericModal
          isOpen={actionModal === TableModalAction.ARCHIVE}
          title={archiveModalTitle}
          onClose={() => { closeActionModal() }}
          maxWidth='sm'
          modalWidth={theme.spacing(136)}
          action={false}
          {...extraModalProps}
          {...archiveModalProps}
        >
          {cloneElementWithProps(archiveAction, { selectedRow })}
        </GenericModal>

      </StyledTableWrapper>
    </>
  )
}

export default CustomTable
